import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { TableCell, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { PaperPlaneIcon, TrashIcon } from '@radix-ui/react-icons'
import { MyImage } from 'components/MyImage'
import { useUI } from 'Editor'
import { useParams } from 'react-router-dom'
import { SelectDlc } from './SelectDlc'
import { AutoCoverSelector } from './AutoCoverSelector'
import { UploadFile, Template } from './types'
import { enqueueCptUploads } from './uploadQueue'

interface UploadItemProps {
  cpt: UploadFile
  handleRemove: (id: string) => void
  handleChange: (id: string, key: string, value: any) => void
  template: Template
  isBatchMutating: boolean
  idx: number
}

export const UploadItem = ({
  cpt,
  handleRemove,
  handleChange,
  template,
  isBatchMutating,
  idx,
}: UploadItemProps) => {
  const { tid } = useParams<{ tid: string }>()
  const lid = useUI((state) => state.lid)

  const handleUpload = async () => {
    if (!tid) return
    await enqueueCptUploads([
      {
        id: cpt.id,
        tid,
        lid,
        dlc: cpt.dlc,
        fileName: cpt.name,
        original: cpt.original,
        trim: cpt.trim,
        ai: cpt.ai,
        autoCover: cpt.autoCover,
        preview: cpt.original,
      },
    ])
    handleRemove(cpt.id)
  }

  const imgError = cpt.w !== template?.ow || cpt.h !== template?.oh

  return (
    <TableRow>
      <TableCell>{idx}</TableCell>
      <TableCell>
        <MyImage
          src={cpt.original}
          alt={cpt.name || cpt.id}
          lazy={false}
          className={cn(
            'h-20 w-20 object-cover transition-all hover:scale-105 aspect-square border bg-white hover:border-primary hover:border-2 rounded-xl',
            imgError && 'border-destructive',
          )}
        />
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <div>ID: {cpt.id}</div>
          <div>
            {cpt.w} x {cpt.h}
          </div>
          <div>{cpt.name}</div>
          <div className="bg-destructive">
            {imgError && (
              <span className="text-destructive-foreground">
                img size error
              </span>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <SelectDlc
          tid={tid!}
          value={cpt.dlc}
          update={(e) => handleChange(cpt.id, 'dlc', e)}
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center space-x-2">
          <Checkbox
            id={`trim-${cpt.id}`}
            checked={cpt.trim}
            onCheckedChange={(checked) => {
              handleChange(cpt.id, 'trim', checked)
              // 取消 trim 时，重置 autoCover 为 0
              if (!checked) {
                handleChange(cpt.id, 'autoCover', 0)
              }
            }}
          />
          <label
            htmlFor={`trim-${cpt.id}`}
            title="裁掉透明留白，便于在捏人区展示；不修改原图尺寸校验（仍须符合模板 ow×oh）"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Trim
          </label>
        </div>
      </TableCell>
      <TableCell>
        {cpt.trim ? (
          <AutoCoverSelector
            value={cpt.autoCover}
            update={(e) => handleChange(cpt.id, 'autoCover', e)}
          />
        ) : (
          <span className="text-muted-foreground text-sm">N/A</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center space-x-2">
          <Checkbox
            id={`ai-${cpt.id}`}
            checked={cpt.ai}
            onCheckedChange={(e) => handleChange(cpt.id, 'ai', e)}
          />
          <label
            htmlFor="ai"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            AI
          </label>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => handleRemove(cpt.id)}>
            <TrashIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="default"
            disabled={imgError || isBatchMutating}
            onClick={handleUpload}
          >
            <PaperPlaneIcon className="mr-2 h-4 w-4 -rotate-45" />
            Upload
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )
}
